/**
 * MODULE: core/tenant/frontend/components/TenantSwitcher.tsx
 * PURPOSE: Tenant switcher dropdown component - Allows users to switch between available tenants
 *
 * DEPENDENCIES:
 *   - react: useState, useRef for component state
 *   - @shared/hooks: useOnClickOutside for dropdown outside click handling
 *   - @shared/utils: cn for className utilities
 *   - ../hooks/useTenant: useTenant hook for tenant context
 *
 * PUBLIC API:
 *   - TenantSwitcher: React component for tenant switching UI
 *
 * SIDE EFFECTS:
 *   - Calls switchTenant() when user selects different tenant
 *   - Updates localStorage with selected tenant ID
 *   - Triggers tenant context updates via useTenant hook
 *
 * AGENT SAFETY: SAFE
 * FILE SIZE: 77 / 200 lines
 *
 * CHANGE LOG:
 *   - 2026-01-20: Created - Tenant switcher dropdown component
 *     Provides UI for switching between available tenants
 *     Handles single tenant vs multiple tenants display
 *     Related: TenantProvider context, useTenant hook
 *
 * See: /README.md#documentation | /ARCHITECTURE.md | /core/tenant/README.md
 */

import { useState, useRef } from 'react';
import { useOnClickOutside } from '@shared/hooks';
import { cn } from '@shared/utils';
import { useTenant } from '../hooks/useTenant';

interface TenantSwitcherProps {
  className?: string;
}

export function TenantSwitcher({ className }: TenantSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { currentTenant, availableTenants, switchTenant, isLoading } = useTenant();

  useOnClickOutside(dropdownRef, () => setIsOpen(false));

  const handleSwitch = async (tenantId: string) => {
    if (tenantId === currentTenant?.id) {
      setIsOpen(false);
      return;
    }

    try {
      await switchTenant(tenantId);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to switch tenant:', error);
    }
  };

  if (availableTenants.length <= 1) {
    return (
      <div className={cn('tenant-switcher-single', className)}>
        {currentTenant?.name || 'No tenant'}
      </div>
    );
  }

  return (
    <div ref={dropdownRef} className={cn('tenant-switcher', className)}>
      <button
        type="button"
        className="tenant-switcher-trigger"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="tenant-switcher-name">
          {currentTenant?.name || 'Select tenant'}
        </span>
        <span className={cn('tenant-switcher-arrow', isOpen && 'open')}>▼</span>
      </button>

      {isOpen && (
        <ul className="tenant-switcher-dropdown" role="listbox">
          {availableTenants.map((tenant) => (
            <li key={tenant.id} role="option" aria-selected={tenant.id === currentTenant?.id}>
              <button
                type="button"
                className={cn(
                  'tenant-switcher-option',
                  tenant.id === currentTenant?.id && 'active'
                )}
                onClick={() => handleSwitch(tenant.id)}
              >
                <span className="tenant-option-name">{tenant.name}</span>
                <span className="tenant-option-slug">{tenant.slug}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
